import React, { FC } from 'react'
import { Typography as MuiTypography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import type { CSSObject } from 'tss-react'
import { Theme } from '@mui/material/styles'
import { ThemeBrandTypographyType, ThemeBrandColorsType } from 'BrandTheme/types'

export type TypographyType = keyof ThemeBrandTypographyType['heading']
export type TypographySizeType = keyof ThemeBrandTypographyType['text']
export type TypographyModifierType = keyof ThemeBrandTypographyType['modifiers']
export type BrandColorType = keyof ThemeBrandColorsType

type BrandTypographyStylesParams = {
  type?: TypographyType
  size?: TypographySizeType
  color?: BrandColorType
  modifiers: TypographyModifierType[]
}

// Key order is the precedence order the four per-prop JSS sheets had in v4
// (color < type < size < modifier); tss merges in className order, see below.
// Rules are copied: tss-react writes a `label` onto each rule object, which would
// otherwise mutate the shared theme objects.
const useBrandTypographyStyles = makeStyles<BrandTypographyStylesParams>()(
  (theme: Theme, { type, size, color, modifiers }) => ({
    textColor: color ? { color: `${theme.brand.colors[color]} !important` } : {},
    textType: { ...(type ? theme.brand.typography.heading[type] : {}) } as CSSObject,
    textSize: { ...(size ? theme.brand.typography.text[size] : {}) } as CSSObject,
    modifier: Object.assign(
      {},
      ...modifiers.map((modifier) => theme.brand.typography.modifiers[modifier])
    ) as CSSObject,
  })
)

export type TypographyPropsType = {
  className?: string
  type?: TypographyType
  size?: TypographySizeType
  modifiers?: TypographyModifierType[]
  color?: BrandColorType
  children?: React.ReactNode
  component?: React.ElementType
  style?: React.CSSProperties
  testId?: string
  html?: boolean
  href?: string
  onClick?: () => void
  'data-componentname'?: string
}

const Typography: FC<TypographyPropsType> = ({
  className,
  type,
  size,
  modifiers,
  color,
  children,
  component,
  style,
  testId,
  html = false,
  href,
  onClick,
  'data-componentname': dataComponentName,
}) => {
  const {
    classes: { textColor, textType, textSize, modifier },
    cx,
  } = useBrandTypographyStyles({ type, size, color, modifiers: modifiers || [] })
  const variant = type || 'body1'
  const extraProps: Record<string, unknown> = {}

  // Support HTML entities
  if (html) {
    extraProps.dangerouslySetInnerHTML = { __html: children }
  }

  // Support link attribute for <a href='...'>
  if (href) {
    extraProps.href = href
  }

  return (
    <MuiTypography
      data-testid={testId}
      data-componentname={dataComponentName}
      // v4 created these sheets at render time, after the caller's, so they won. `cx` merges
      // into one class with later arguments winning.
      className={cx(className, textColor, textType, textSize, modifier)}
      data-component='typography'
      gutterBottom
      variant={variant}
      component={component}
      style={style}
      onClick={onClick}
      {...extraProps}
    >
      {!html ? children : null}
    </MuiTypography>
  )
}

export default Typography
